import { STATUS_DE_VEICULO } from '@locaveri/shared'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'

import { apiGet } from '@/lib/api'
import { ROTAS_DE_BI } from '@/lib/endpoints'

/**
 * O BI no painel — a camada de dados.
 *
 * ⚠️ NADA AQUI RECALCULA NADA. Ocupação, RevPAC, margem, payback e conversão
 * vêm prontos da API, que usa as funções de `@locaveri/shared/metricas`. Um
 * segundo cálculo no painel é exatamente como a tela e a exportação passam a
 * discordar sobre o mesmo mês — e a discussão quem perde é sempre a empresa,
 * porque foi ela que mostrou os dois números.
 *
 * ⚠️ `null` CHEGA COMO `null` E FICA. Ocupação `0%` = o carro existiu e ficou
 * parado; `null` = não dá para responder. Um `?? 0` em qualquer ponto deste
 * arquivo apagaria a distinção que a API inteira foi desenhada para preservar.
 */

const dinheiroNoFio = z.string().regex(/^-?\d+$/u)

export const metricaDoVeiculoSchema = z.object({
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  categoriaNome: z.string(),

  diasDePosse: z.number().int(),
  diasDisponiveis: z.number().int(),
  diasAlugados: z.number().int(),
  diasEmContratoLongo: z.number().int(),
  diasDeOciosidade: z.number().int(),

  taxaDeOcupacao: z.number().int().nullable(),
  taxaDeUtilizacao: z.number().int().nullable(),
  taxaDeOcupacaoDeDiaria: z.number().int().nullable(),

  receita: dinheiroNoFio,
  custoDireto: dinheiroNoFio,
  margem: dinheiroNoFio,
  margemPercentual: z.number().int().nullable(),
  revPac: dinheiroNoFio.nullable(),
  diariaMediaRealizada: dinheiroNoFio.nullable(),
  custoPorDiaDePosse: dinheiroNoFio.nullable(),
  custoDaOciosidade: dinheiroNoFio.nullable(),

  kmRodado: z.number().int(),
  custoPorKm: dinheiroNoFio.nullable(),
  custoDeUpgradeAbsorvido: dinheiroNoFio,
})

export type MetricaDoVeiculo = z.infer<typeof metricaDoVeiculoSchema>

export const visaoDeFrotaSchema = z.object({
  de: z.iso.date(),
  ate: z.iso.date(),
  diasNaJanela: z.number().int(),
  veiculos: z.array(metricaDoVeiculoSchema),
  total: metricaDoVeiculoSchema.omit({
    vehicleId: true,
    placa: true,
    modelo: true,
    categoriaNome: true,
  }),
})

export type VisaoDeFrota = z.infer<typeof visaoDeFrotaSchema>

export const visaoDeDemandaSchema = z.object({
  de: z.iso.date(),
  ate: z.iso.date(),
  categorias: z.array(
    z.object({
      categoryId: z.uuid(),
      categoriaNome: z.string(),
      buscasSemDisponibilidade: z.number().int(),
      porFaltaDeCarro: z.number().int(),
      porFaltaDeCadastro: z.number().int(),
      diariasMedias: z.number().int().nullable(),
    }),
  ),
  porOrigem: z.array(
    z.object({ origem: z.enum(['SITE', 'APP', 'PAINEL']), buscas: z.number().int() }),
  ),
})

export type VisaoDeDemanda = z.infer<typeof visaoDeDemandaSchema>

export const visaoDeFunilSchema = z.object({
  de: z.iso.date(),
  ate: z.iso.date(),
  cotacoes: z.number().int(),
  /** Uma por chamada de cotação. É o denominador da conversão. */
  buscas: z.number().int(),
  reservas: z.number().int(),
  taxaDeConversao: z.number().int().nullable(),
  canceladas: z.number().int(),
  taxaDeCancelamento: z.number().int().nullable(),
  noShows: z.number().int(),
  taxaDeNoShow: z.number().int().nullable(),
  ticketMedio: dinheiroNoFio.nullable(),
  leadTimeMedioEmDias: z.number().int().nullable(),
  motivosDeCancelamento: z.array(
    z.object({
      motivoId: z.uuid().nullable(),
      rotulo: z.string(),
      quantidade: z.number().int(),
    }),
  ),
})

export type VisaoDeFunil = z.infer<typeof visaoDeFunilSchema>

export interface JanelaDeBi {
  readonly de: string
  readonly ate: string
  readonly categoryId?: string
  readonly locationId?: string
}

export const CHAVE_DE_BI = ['bi'] as const

export function useVisaoDeFrota(janela: JanelaDeBi): UseQueryResult<VisaoDeFrota> {
  return useQuery({
    queryKey: [...CHAVE_DE_BI, 'frota', janela],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_BI.frota(janela), visaoDeFrotaSchema, { signal }),
  })
}

/**
 * A frota da OPERAÇÃO — ocupação e ociosidade sem nenhum valor em dinheiro.
 *
 * Schema próprio, e não `metricaDoVeiculoSchema.pick(...)`, pelo mesmo motivo
 * da API: uma lista positiva só ganha campo quando alguém o escreve aqui.
 */
export const ocupacaoDoVeiculoSchema = z.object({
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  categoryId: z.uuid(),
  categoriaNome: z.string(),
  statusAtual: z.enum(STATUS_DE_VEICULO),

  diasDePosse: z.number().int(),
  diasDisponiveis: z.number().int(),
  diasBloqueados: z.number().int(),
  diasAlugados: z.number().int(),
  /** Pronto e sem locação. NÃO é a ociosidade da visão completa — não conta a oficina. */
  diasParadoDisponivel: z.number().int(),

  taxaDeOcupacao: z.number().int().nullable(),
  taxaDeUtilizacao: z.number().int().nullable(),
  kmRodado: z.number().int(),
})

export type OcupacaoDoVeiculo = z.infer<typeof ocupacaoDoVeiculoSchema>

export const visaoDeOcupacaoSchema = z.object({
  de: z.iso.date(),
  ate: z.iso.date(),
  diasNaJanela: z.number().int(),
  veiculos: z.array(ocupacaoDoVeiculoSchema),
  total: z.object({
    diasDePosse: z.number().int(),
    diasDisponiveis: z.number().int(),
    diasBloqueados: z.number().int(),
    diasAlugados: z.number().int(),
    diasParadoDisponivel: z.number().int(),
    taxaDeOcupacao: z.number().int().nullable(),
    taxaDeUtilizacao: z.number().int().nullable(),
    kmRodado: z.number().int(),
  }),
})

export type VisaoDeOcupacao = z.infer<typeof visaoDeOcupacaoSchema>

export function useVisaoDeOcupacao(janela: JanelaDeBi): UseQueryResult<VisaoDeOcupacao> {
  return useQuery({
    queryKey: [...CHAVE_DE_BI, 'ocupacao', janela],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_BI.ocupacao(janela), visaoDeOcupacaoSchema, { signal }),
  })
}

export function useVisaoDeDemanda(janela: JanelaDeBi): UseQueryResult<VisaoDeDemanda> {
  return useQuery({
    queryKey: [...CHAVE_DE_BI, 'demanda', janela],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_BI.demanda(janela), visaoDeDemandaSchema, { signal }),
  })
}

export function useVisaoDeFunil(janela: JanelaDeBi): UseQueryResult<VisaoDeFunil> {
  return useQuery({
    queryKey: [...CHAVE_DE_BI, 'funil', janela],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_BI.funil(janela), visaoDeFunilSchema, { signal }),
  })
}
